import { FormService, Logger } from '@core/services';
import { EventVenueEntity, PaginatedEntity, Pagination, TagEntity, TagsListEntity } from '@core/entities';
import { FormBuilder } from '@angular/forms';
import { untilDestroyed } from '@ngneat/until-destroy';
import { inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HotToastService } from '@ngxpert/hot-toast';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from '@ngneat/dialog';
import { NgxBottomSheetService } from 'ngx-bottom-sheet';
import { ConfirmationDialogComponent, SingleInputCreateComponent } from '@web/_shared/dialogs';
import { ConfirmationDialogType, DeleteConfirmationOptions, TagType } from '@core/enums';
import { isMobileView } from '@core/utils';
import { DefaultDeleteDialogConfig, MDeleteConfirmationDialogComponent } from '@mobile/_shared/dialogs';
import { CreateTagFormComponent } from '@mobile/_shared/sheets';
import { PERMISSIONS, PermissionService } from '@auth';
import { UseEventVenue, UseTag } from '@core/usecases';

const log = new Logger('Tags Composition');

export class TagsComposition extends FormService {
  tags: TagEntity[] = [];
  editMode = false;
  pagination: Pagination = new PaginatedEntity({
    pagination: {
      page: 1,
      pageSize: 20,
    },
  }).pagination;
  currentTabRoute = 'instructor';
  search = '';
  protected readonly _route = inject(ActivatedRoute);
  protected readonly _translate = inject(TranslateService);
  private readonly _toast = inject(HotToastService);
  private readonly _dialogService = inject(DialogService);
  private readonly _bottomSheet = inject(NgxBottomSheetService);
  private readonly _router = inject(Router);
  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _permissionService: PermissionService = inject(PermissionService);

  private readonly _useTags = new UseTag();
  private readonly _useEventVenues = new UseEventVenue();

  get canCreateTag(): boolean {
    return this._permissionService.hasPermission(PERMISSIONS.CREATE_TAGS);
  }

  get isEventVenueTabSelected(): boolean {
    return this.currentTabRoute === TagType.EVENT_VENUES;
  }

  onSearch() {
    this.pagination = {
      page: 1,
      pageSize: 20,
    };
    this.isLoading = true;
    this._fetchAllTags();
  }

  onClearSearch(event: Event) {
    if ((event.target as HTMLInputElement).value === '') {
      this.onSearch();
    }
  }

  createNewTag() {
    let service$;

    const title = this.isEventVenueTabSelected ? 'Create Event Venue' : 'Create New Tag';
    const label = this.isEventVenueTabSelected ? 'Event Venue Name' : 'Tag Name';

    if (isMobileView()) {
      service$ = this._bottomSheet.open(CreateTagFormComponent, {
        height: '35%',
        showCloseButton: false,
        data: {
          title: this._translate.instant(title),
          label: this._translate.instant(label),
          tagType: this.currentTabRoute,
        },
      });
    } else {
      service$ = this._dialogService.open(SingleInputCreateComponent, {
        size: 'md',
        closeButton: false,
        data: {
          title: this._translate.instant(title),
          label: this._translate.instant(label),
          tagType: this.currentTabRoute,
        },
      });
    }

    service$.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (res) => {
        if (res) {
          if (this.isEventVenueTabSelected) {
            this._createEventVenue(res);
          } else {
            this._createTag(res, this.currentTabRoute as TagType);
          }
        }
      },
    });
  }

  updateTag(tag: TagEntity) {
    if (!tag.id) return;

    const title = this.isEventVenueTabSelected ? 'Update Event Venue' : 'Update Tag';
    const label = this.isEventVenueTabSelected ? 'Event Venue Name' : 'Tag Name';
    let service$;

    if (isMobileView()) {
      service$ = this._bottomSheet.open(CreateTagFormComponent, {
        height: '35%',
        showCloseButton: false,
        data: {
          title: this._translate.instant(title),
          label: this._translate.instant(label),
          tagType: this.currentTabRoute,
          value: tag.tagName,
        },
      });
    } else {
      service$ = this._dialogService.open(SingleInputCreateComponent, {
        size: 'md',
        closeButton: false,
        data: {
          title: this._translate.instant(title),
          label: this._translate.instant(label),
          tagType: this.currentTabRoute,
          value: tag.tagName,
        },
      });
    }

    if (!service$) return;
    service$.afterClosed$.pipe(untilDestroyed(this)).subscribe({
      next: (res) => {
        if (res) {
          if (this.isEventVenueTabSelected) {
            this._updateEventVenue(tag.id, res);
          } else {
            tag.tagName = res;
            this._updateTag(tag);
          }
        }
      },
    });
  }

  deleteTag(tag: TagEntity) {
    if (!tag.id) return;

    if (isMobileView()) {
      const confirmDeleteSheet = this._bottomSheet.open(MDeleteConfirmationDialogComponent, {
        ...DefaultDeleteDialogConfig,
        data: {
          text: this._translate.instant('Tag'),
        },
      });
      confirmDeleteSheet.afterClosed$.pipe(untilDestroyed(this)).subscribe({
        next: (res) => {
          switch (res) {
            case DeleteConfirmationOptions.DELETE:
              if (res) {
                if (this.isEventVenueTabSelected) {
                  this._deleteEventVenue(tag.id);
                } else {
                  this._deleteTag(tag);
                }
              }
              break;
            default:
              break;
          }
        },
      });
    } else {
      this._dialogService
        .open(ConfirmationDialogComponent, {
          closeButton: false,
          data: {
            type: ConfirmationDialogType.DELETE,
            emphasize: tag.tagName,
          },
        })
        .afterClosed$.pipe(untilDestroyed(this))
        .subscribe({
          next: (res) => {
            if (res) {
              if (this.isEventVenueTabSelected) {
                this._deleteEventVenue(tag.id);
              } else {
                this._deleteTag(tag);
              }
            }
          },
        });
    }
  }

  onTabChange(tabName: string) {
    if (tabName.toLowerCase() === this.currentTabRoute.toLowerCase()) return;
    if (tabName === 'more') return;
    this.currentTabRoute = tabName.toLowerCase();

    if (tabName === TagType.EVENT_VENUES) {
      this.pagination = new PaginatedEntity({
        pagination: {
          page: 0,
          pageSize: 0,
          rowCount: 0,
        },
      }).pagination;
      this._fetchAllEventVenues();
    } else {
      this.pagination = new PaginatedEntity({
        pagination: {
          page: 1,
          pageSize: 20,
        },
      }).pagination;
      if (isMobileView()) this.pagination.pageSize = 99999;
      this._fetchAllTags();
    }
  }

  triggerEditMode() {
    this.editMode = !this.editMode;
  }

  saveTagState() {
    this.editMode = false;
  }

  // ---------------------------------------------------------------------------------------------------------------------
  // ------------------------------------------ Private Protected Functions ----------------------------------------------
  // ---------------------------------------------------------------------------------------------------------------------

  protected paginationChange(pagination: Pagination) {
    this.pagination = pagination;
    this.isLoading = true;

    this._fetchAllTags();
  }

  protected _fetchAllTags() {
    this.call(this._useTags.getAllAvailableTags(this.pagination, this.currentTabRoute as TagType, this.search))
      .then((res: TagsListEntity) => {
        if (res) {
          this.pagination = res.pagination;
          this.tags = res.tags;
        } else {
          log.info('No Tag found');
        }
      })
      .catch((err) => {
        log.error('Error while fetching Tags', err);
      });
  }

  private _fetchAllEventVenues() {
    this.call(this._useEventVenues.getAllEventVenues())
      .then((res) => {
        if (res) {
          this.tags = res.map((venue: EventVenueEntity) => {
            return {
              id: venue.id,
              tagName: venue.venue,
              type: TagType.EVENT_VENUES,
              timeStamp: venue.createdAt,
            } as TagEntity;
          });
        } else {
          log.info('No Event Venue found');
        }
      })
      .catch((err) => {
        log.error('Error while fetching Event Venues', err);
      });
  }

  private _createTag(tagName: string, tagType: TagType) {
    this.call(this._useTags.createTag(tagName, tagType))
      .then((res) => {
        if (res) {
          this._toast.success(this._translate.instant('Created Successfully'));
          this._fetchAllTags();
        } else {
          log.error('Error while creating Tag');
        }
      })
      .catch((err) => {
        log.error('Error while creating Tag', err);
      });
  }

  private _updateTag(tag: TagEntity) {
    this.call(this._useTags.updateTag(tag))
      .then((res) => {
        if (res) {
          this._toast.success(this._translate.instant('Update Successful'));
          this._fetchAllTags();
        } else {
          log.error('Error while updating Tag');
        }
      })
      .catch((err) => {
        log.error('Error while updating Tag', err);
      });
  }

  private _deleteTag(tag: TagEntity) {
    this.call(this._useTags.deleteTag(tag.id))
      .then((res) => {
        if (res) {
          this._toast.success(this._translate.instant('Delete Successful'));
          this._fetchAllTags();
        } else {
          log.error('Error while deleting Tag');
        }
      })
      .catch((err) => {
        log.error('Error while deleting Tag', err);
      });
  }

  private _createEventVenue(venueName: string) {
    this.call(this._useEventVenues.createEventVenue({ venue: venueName }))
      .then((res) => {
        if (res) {
          this._toast.success(this._translate.instant('Created Successfully'));
          this._fetchAllEventVenues();
        } else {
          log.error('Error while creating Event Venue');
        }
      })
      .catch((err) => {
        log.error('Error while creating Event Venue', err);
      });
  }

  private _updateEventVenue(venueId: number, venueName: string) {
    this.call(this._useEventVenues.updateEventVenue(venueId, { venue: venueName }))
      .then((res) => {
        if (res) {
          this._toast.success(this._translate.instant('Update Successful'));
          this._fetchAllEventVenues();
        } else {
          log.error('Error while updating Event Venue');
        }
      })
      .catch((err) => {
        log.error('Error while updating Event Venue', err);
      });
  }

  private _deleteEventVenue(id: number) {
    this.call(this._useEventVenues.deleteEventVenue(id))
      .then((res) => {
        if (res) {
          this._toast.success(this._translate.instant('Delete Successful'));
          this._fetchAllEventVenues();
        } else {
          log.error('Error while deleting Event Venue');
        }
      })
      .catch((err) => {
        log.error('Error while deleting Event Venue', err);
      });
  }
}
